import { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const navigate = useNavigate();

  // Fallback static events data (shown if no data in Firestore)
  const fallbackEvents = [
    {
      id: 1,
      title: "Exeter Market Day",
      date: "Sun 23 Oct",
      image: "/images/exeter-market.jpg",
      description: "Join us at the vibrant Exeter Markets for a day of local business showcase and community connection."
    },
    {
      id: 2,
      title: "Social Night @ Cabbage Tree Wines",
      date: "Thu 3 Mar",
      image: "/images/cabbage-tree-wines.jpg",
      description: "Network with fellow business owners while enjoying fine local wines in a relaxed atmosphere."
    },
    {
      id: 3,
      title: "Business Workshop @ Sidmouth Hall",
      date: "Tue 16 Feb",
      image: "/images/business-workshop.jpg",
      description: "Professional development workshop covering essential business skills and growth strategies."
    },
    {
      id: 4,
      title: "Progressive Dinner in Beaconsfield",
      date: "Wed 14 Oct",
      image: "/images/progressive-dinner.jpg",
      description: "A unique dining experience that brings together local businesses and community members."
    }
  ];

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch events from Firestore
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'events'));
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Use Firestore data if available, otherwise use fallback
      setEvents(eventsData.length > 0 ? eventsData : fallbackEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      // Use fallback data if Firestore fetch fails
      setEvents(fallbackEvents);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'events', eventId));
      alert('Event deleted successfully!');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const handleImageLoad = (eventId) => {
    setImageLoading(prev => ({
      ...prev,
      [eventId]: false
    }));
  };

  const handleImageError = (eventId) => {
    setImageLoading(prev => ({
      ...prev,
      [eventId]: false
    }));
  };

  return (
    <section id="events" className="py-20">
      <div className="container-width section-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Upcoming <span className="text-secondary">Events</span>
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Embrace the chance to expand your network, foster meaningful connections, and tap into a wealth of local expertise.
          </p>
          
          {/* Admin Controls */}
          {user && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/manage-events')}
                className="btn-primary inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Manage Events
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-white py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <p className="mt-4">Loading events...</p>
          </div>
        ) : (
          <>
            {/* Events Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {events.map((event) => (
                <div 
                  key={event.id}
                  className="overflow-hidden group relative"
                >
                  {/* Event Image */}
                  <div className="relative h-48 overflow-hidden bg-gray-800">
                    {imageLoading[event.id] !== false && (
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse"></div>
                    )}
                    <img 
                      src={event.image} 
                      alt={event.title}
                      className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${imageLoading[event.id] === false ? 'opacity-100' : 'opacity-0'}`}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => handleImageLoad(event.id)}
                      onError={() => handleImageError(event.id)}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {event.date}
                      </span>
                    </div>
                  </div>

                  {/* Event Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-300 transition-colors duration-200">
                      {event.title}
                    </h3>
                    <p className="text-gray-200 text-sm leading-relaxed">
                      {event.description}
                    </p>
                  </div>

                  {/* Quick Delete for Admins */}
                  {user && typeof event.id === 'string' && (
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-colors"
                        title="Delete event"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="text-center mt-16">
              <div className="p-8 md:p-12">
                <h3 className="text-3xl font-bold text-white mb-6">
                  Ready to Join Our Community?
                </h3>
                <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
                  For any membership inquiries, partnership opportunities, or general information, please feel free to 
                  contact us or visit us at the Exeter markets. We are always delighted to welcome new members and 
                  collaborate with individuals who share our vision for a thriving business community in the Tamar Valley.
                </p>
                <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
                  Remember, together we can nurture economic growth, create sustainable businesses, and strengthen the 
                  Tamar Valley's position as a hub for innovation and prosperity. Join TVBA today and take advantage of 
                  the endless possibilities that await you in our breathtaking region!
                </p>
                <div className="flex justify-center">
                  <a 
                    href="/contact"
                    className="btn-primary"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default EventsPage
